import { Component, OnInit } from '@angular/core';
import { ServiceService } from '../service.service';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

/**
 * @class AttendanceRequestEditComponent
 * @description This component is responsible for editing an attendance request. 
 * It fetches the details of an existing attendance request, displays them in a form, 
 * and allows the user to update the request. The form includes fields for employee name, 
 * requested date, and status, all of which are required.
 * 
 * @implements OnInit
 */
@Component({
  selector: 'app-attendance-request-edit',
  templateUrl: './attendance-request-edit.component.html',
  styleUrls: ['./attendance-request-edit.component.css']
})
export class AttendanceRequestEditComponent implements OnInit {

  /**
   * @property {FormGroup} creationForm - The reactive form used to handle the attendance request data.
   * It includes fields for `_id`, `employee_id`, `employee_name`, `requested_for_date`, and `status`.
   */
  creationForm: FormGroup;

  /**
   * @property {string[]} Status - An array of possible statuses for the attendance request.
   * The available statuses are 'Requested', 'Approved', and 'Rejected'.
   */
  Status: string[] = ['Requested', 'Approved', 'Rejected'];

  /**
   * @constructor
   * @param {ServiceService} service - The service used to interact with the backend API for fetching and updating attendance requests.
   * 
   * @param {ActivatedRoute} activatedRoute - Provides access to the route parameters, such as the attendance request ID.
   * 
   * @param {Location} location - Used to navigate back to the previous page.
   * 
   * @param {FormBuilder} fb - Angular's FormBuilder service for creating the reactive form.
   */
  constructor(
    private service: ServiceService,
    private activatedRoute: ActivatedRoute,
    private location: Location,
    private fb: FormBuilder
  ) {
    // Initialize the reactive form with required fields and validators
    this.creationForm = this.fb.group({
      _id: [null, Validators.required],
      employee_id: ['', Validators.required],
      employee_name: ['', Validators.required],
      requested_for_date: ['', Validators.required],
      status: ['', Validators.required],
    });
  }

  /**
   * @method ngOnInit
   * @description Lifecycle hook that is called after the component is initialized.
   * It retrieves the attendance request ID from the route parameters and fetches the request details.
   */
  ngOnInit(): void {
    const id = this.activatedRoute.snapshot.queryParams['id'];
    
    id ? this.getDetails(id) : (alert('Invalid request. Redirecting back.'), this.location.back());
  }

  /**
   * @method getDetails
   * @description Fetches the details of an attendance request from the backend using the provided ID.
   * The fetched data is used to populate the form fields.
   * 
   * @param {string | null} id - The ID of the attendance request to fetch.
   */
  getDetails(id: string | null): void {
    this.service.viewAttendanceRequestDetails(id).subscribe({
      next: (res) => {
        this.creationForm.patchValue({
          _id: res.data[0]._id,
          employee_id: res.data[0].employee_id,
          employee_name: res.data[0].employee_name,
          requested_for_date: res.data[0].requested_for_date,
          status: res.data[0].status,
        });
      },
      error: (err) => {
        console.error('Error fetching attendance request details:', err);
        alert('Failed to fetch details. Redirecting back.');
        this.location.back();
      },
    });
  }

  /**
   * @method submit
   * @description Submits the updated attendance request data to the backend.
   * If the form is valid, it sends the data to the API and navigates back on success.
   * If the form is invalid, it marks all fields as touched to display validation errors.
   */
  submit(): void {
    if (this.creationForm.valid) {
      this.service.updateAttendanceRequest(this.creationForm.value).subscribe({
        next: (res) => {
          if (res.success) {
            this.service.showToast('success', res.message);
            this.location.back();
          }
        },
        error: (err) => {
          console.error('Error updating attendance request:', err);
          alert('Failed to update. Please try again.');
        },
      });
    } else {
      this.creationForm.markAllAsTouched();
    }
  }
}